import React, { useContext, useEffect, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import Select from 'react-select';
import ButtonV2 from 'components/common/ButtonV2';
import { axiosGetAPI, axiosPost } from 'services';
import { authContext } from 'contexts/AuthContext';
import {
  CITY_BY_STATE,
  STATES_BY_COUNTRY,
} from 'pages/MarketListing/MarketListingGraphQL';

/* ------------------------------------------------------------------ */
/* Static data                                                         */
/* ------------------------------------------------------------------ */
const INDIA_COUNTRY_ID = '101';
let cachedCityOptions = null;

/* Combinations that have data */
// eslint-disable-next-line no-unused-vars
const HAS_DATA = new Set([
  'Mumbai|Delhi|Mini Truck (1 Ton)',
  'Mumbai|Delhi|Small Truck (2 Ton)',
  'Mumbai|Delhi|Medium Truck (5 Ton)',
  'Mumbai|Delhi|Large Truck (10 Ton)',
  'Mumbai|Delhi|Container (20 Ton)',
  'Mumbai|Delhi|Container (32 Ton)',
  'Mumbai|Delhi|Trailer (40 Ton)',
  'Delhi|Mumbai|Mini Truck (1 Ton)',
  'Delhi|Bangalore|Medium Truck (5 Ton)',
  'Bangalore|Delhi|Large Truck (10 Ton)',
  'Chennai|Hyderabad|Mini Truck (1 Ton)',
  'Hyderabad|Chennai|Small Truck (2 Ton)',
  'Ahmedabad|Mumbai|Container (20 Ton)',
  'Pune|Delhi|Medium Truck (5 Ton)',
  'Kolkata|Delhi|Large Truck (10 Ton)',
]);

// eslint-disable-next-line no-unused-vars
const STATIC_RESULT = {
  approxDistance: '1,418 km',
  transitTime: '2 – 3 Days',
  approxDeliverRate: '₹ 42,500',
  route: ['Mumbai', 'Nashik', 'Aurangabad', 'Nagpur', 'Bhopal', 'Gwalior', 'Agra', 'Delhi'],
};

// Route-wise distance, time, rate data
// eslint-disable-next-line no-unused-vars
const ROUTE_DATA = {
  direct: {
    distance: '1,418 km',
    time: '2 – 3 Days',
    rate: '₹ 42,500',
    ratePerKg: '₹ 30/kg',
  },
  indirectOption1: {
    segments: [
      { from: 'Mumbai', to: 'Ahmedabad', distance: '500 km', time: '1 Day', rate: '₹ 20,000' },
      { from: 'Ahmedabad', to: 'Delhi', distance: '450 km', time: '1 Day', rate: '₹ 18,000' },
    ],
    total: {
      distance: '950 km',
      time: '1 – 2 Days',
      rate: '₹ 38,000',
      ratePerKg: '₹ 40/kg',
    },
  },
  indirectOption2: {
    segments: [
      { from: 'Mumbai', to: 'Agra', distance: '700 km', time: '1.5 Days', rate: '₹ 25,000' },
      { from: 'Agra', to: 'Delhi', distance: '220 km', time: '0.5 Days', rate: '₹ 8,000' },
    ],
    total: {
      distance: '920 km',
      time: '2 Days',
      rate: '₹ 33,000',
      ratePerKg: '₹ 35/kg',
    },
  },
};

// A route may have one or more indirect alternatives.
// eslint-disable-next-line no-unused-vars
const INDIRECT_ROUTE_OPTIONS = {
  'Mumbai|Delhi': [['Mumbai', 'Ahmedabad', 'Delhi'], ['Mumbai', 'Agra', 'Delhi']],
  'Delhi|Mumbai': [['Delhi', 'Ahmedabad', 'Mumbai']],
  'Delhi|Bangalore': [['Delhi', 'Nagpur', 'Bangalore']],
  'Bangalore|Delhi': [['Bangalore', 'Hyderabad', 'Delhi']],
  'Chennai|Hyderabad': [['Chennai', 'Bangalore', 'Hyderabad']],
  'Hyderabad|Chennai': [['Hyderabad', 'Bangalore', 'Chennai']],
  'Ahmedabad|Mumbai': [['Ahmedabad', 'Surat', 'Mumbai']],
  'Pune|Delhi': [['Pune', 'Indore', 'Delhi']],
  'Kolkata|Delhi': [['Kolkata', 'Varanasi', 'Delhi']],
  'Chennai|Delhi': [
    ['Chennai', 'Bangalore', 'Delhi'],
    ['Chennai', 'Hyderabad', 'Nagpur', 'Delhi'],
  ],
};

/* ------------------------------------------------------------------ */
/* Custom react-select styles — consistent with project theme         */
/* ------------------------------------------------------------------ */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '42px',
    borderColor: state.isFocused ? '#1d4ed8' : '#d0d5dd',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(29,78,216,0.1)' : 'none',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#101828',
    '&:hover': { borderColor: '#1d4ed8' },
  }),
  placeholder: (base) => ({ ...base, color: '#98a2b3', fontSize: '14px' }),
  singleValue: (base) => ({ ...base, color: '#101828', fontSize: '14px' }),
  option: (base, state) => {
    let backgroundColor = '#fff';
    if (state.isSelected) backgroundColor = '#eff4ff';
    else if (state.isFocused) backgroundColor = '#f8fafc';
    return {
      ...base,
      fontSize: '14px',
      backgroundColor,
      color: state.isSelected ? '#1d4ed8' : '#101828',
      fontWeight: state.isSelected ? 600 : 400,
      cursor: 'pointer',
    };
  },
  menu: (base) => ({ ...base, borderRadius: '8px', boxShadow: '0 8px 24px rgba(16,24,40,0.12)', zIndex: 20 }),
  indicatorSeparator: () => ({ display: 'none' }),
};

const formatNumber = (value) => Number(value || 0).toLocaleString('en-IN');
const formatDistance = (value) => `${formatNumber(value)} km`;
const formatDays = (value) => `${value} ${Number(value) === 1 ? 'Day' : 'Days'}`;
const formatRate = (value) => `₹ ${formatNumber(value)}`;
const formatPerKgRate = (value) => `₹ ${Number(value || 0).toFixed(2)}/kg`;

// Calculates divisor (in kg) from vehicle type label e.g. "10 MT" → 10000
const getVehicleDivisorKg = (vehicleTypeObj) => {
  if (!vehicleTypeObj) return null;
  const label = String(vehicleTypeObj.label || vehicleTypeObj.value || '');
  const match = label.match(/(\d+(?:\.\d+)?)\s*MT/i);
  if (match) return parseFloat(match[1]) * 1000;
  // Fallback: try tonnage field
  if (vehicleTypeObj.tonnage) return Number(vehicleTypeObj.tonnage) * 1000;
  return null;
};

// Calculate rate/kg on frontend: ftlRate / divisorKg
const calcRatePerKg = (ftlRate, divisorKg) => {
  if (!divisorKg || !ftlRate) return formatPerKgRate(0);
  return formatPerKgRate(Number(ftlRate) / divisorKg);
};

const mapApiRoute = (route, vehicleType) => {
  const legs = route?.legs || [];
  const stops = legs.length
    ? [legs[0].originCityName, ...legs.map((leg) => leg.destinationCityName)]
    : [route?.originCityName, route?.destinationCityName];

  const divisorKg = getVehicleDivisorKg(vehicleType);

  return {
    stops,
    routeData: {
      segments: legs.length
        ? legs.map((leg) => ({
          distance: formatDistance(leg.distanceKm),
          time: formatDays(leg.transitTimeDays),
          rate: formatRate(leg.ftlRate),
        }))
        : undefined,
      distance: formatDistance(route?.distanceKm),
      time: formatDays(route?.transitTimeDays),
      rate: formatRate(route?.ftlRate),
      ratePerKg: calcRatePerKg(route?.ftlRate, divisorKg),
      total: {
        distance: formatDistance(route?.distanceKm),
        time: formatDays(route?.transitTimeDays),
        rate: formatRate(route?.ftlRate),
        ratePerKg: calcRatePerKg(route?.ftlRate, divisorKg),
      },
    },
  };
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
const renderRouteWithInfo = (type, stops, routeData) => {
  const isIndirect = type.startsWith('Indirect');
  const routeLabel = isIndirect ? `Indirect Route ${type.replace('Indirect ', '')}` : 'Direct Route';

  // Check if it's a direct route (simple data) or indirect (with segments)
  const isDirect = !routeData.segments;

  return (
    <div className="sr-route-row">
      {/* Left: Route */}
      <div className="sr-route-card-inline">
        <div className="sr-route-heading">
          <h5 className="sr-route-title">{routeLabel}</h5>
          <span className={`sr-route-type sr-route-type--${isIndirect ? 'indirect' : 'direct'}`}>{routeLabel}</span>
        </div>
        {isIndirect && <p className="sr-route-summary">Via {stops.slice(1, -1).join(', ')}</p>}

        {/* Route Track with Segment Info for Indirect Routes */}
        {isIndirect && routeData.segments ? (
          <div className="sr-route-track">
            {stops.map((stop, idx) => {
              const isFirst = idx === 0;
              const isLast = idx === stops.length - 1;
              let dotClass = 'sr-route-dot sr-route-dot--mid';
              if (isFirst) dotClass = 'sr-route-dot sr-route-dot--start';
              else if (isLast) dotClass = 'sr-route-dot sr-route-dot--end';

              // Get segment info for this connection
              const segmentInfo = idx < routeData.segments.length ? routeData.segments[idx] : null;

              return (
                <React.Fragment key={`${type}-${stop}`}>
                  <div className="sr-route-stop">
                    <div className={dotClass} />
                    <span className={`sr-route-name ${isFirst || isLast ? 'sr-route-name--bold' : ''}`}>{stop}</span>
                  </div>
                  {idx < stops.length - 1 && (
                    <div className="sr-route-line-wrap">
                      {segmentInfo && (
                        <div className="sr-segment-values">
                          {segmentInfo.distance} • {segmentInfo.time} • {segmentInfo.rate}
                        </div>
                      )}
                      <div className="sr-route-line" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ) : (
          <div className="sr-route-track sr-route-track--direct">
            {stops.map((stop, idx) => {
              const isFirst = idx === 0;
              const isLast = idx === stops.length - 1;
              let dotClass = 'sr-route-dot sr-route-dot--mid';
              if (isFirst) dotClass = 'sr-route-dot sr-route-dot--start';
              else if (isLast) dotClass = 'sr-route-dot sr-route-dot--end';
              return (
                <React.Fragment key={`${type}-${stop}`}>
                  <div className="sr-route-stop">
                    <div className={dotClass} />
                    <span className={`sr-route-name ${isFirst || isLast ? 'sr-route-name--bold' : ''}`}>{stop}</span>
                  </div>
                  {idx < stops.length - 1 && <div className="sr-route-line" />}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      {/* Right: Distance, Time, Rate - 2 Lines */}
      <div className="sr-route-info-box">
        <div className="sr-route-info-stacked">
          <div className="sr-info-line">
            <span className="sr-info-item-label">Distance:</span>
            <span className="sr-info-item-value">{isDirect ? routeData.distance : routeData.total.distance}</span>
            <span className="sr-info-item-sep">•</span>
            <span className="sr-info-item-label">Time:</span>
            <span className="sr-info-item-value">{isDirect ? routeData.time : routeData.total.time}</span>
          </div>
          <div className="sr-info-line">
            <span className="sr-info-item-label">Rate:</span>
            <span className="sr-info-item-value sr-info-item-value--rate">{isDirect ? routeData.rate : routeData.total.rate}</span>
            <span className="sr-info-item-sep">•</span>
            <span className="sr-info-item-label">Rate/kg:</span>
            <span className="sr-info-item-value sr-info-item-value--rate">{isDirect ? routeData.ratePerKg : routeData.total.ratePerKg}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const SearchRateTab = ({ onAddRequest }) => {
  const { auth } = useContext(authContext);
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [vehicleType, setVehicleType] = useState(null);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [vehicleOptionsLoading, setVehicleOptionsLoading] = useState(true);
  const [cityOptions, setCityOptions] = useState(cachedCityOptions || []);
  const [citiesLoading, setCitiesLoading] = useState(!cachedCityOptions);
  const [searched, setSearched] = useState(false);
  const [requested, setRequested] = useState(false);
  const [searchResponse, setSearchResponse] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [sameCityError, setSameCityError] = useState(false);

  const isValid = origin && destination && vehicleType;
  const hasSameCity = Boolean(origin && destination && String(origin.value) === String(destination.value));
  const originName = origin?.cityName || origin?.value;
  const destinationName = destination?.cityName || destination?.value;
  const apiRouteData = searchResponse?.data?.data;
  const directRouteResult = apiRouteData?.directRoute
    ? mapApiRoute(apiRouteData.directRoute, vehicleType)
    : null;
  const indirectRouteResults = (apiRouteData?.indirectRoutes || []).map((r) => mapApiRoute(r, vehicleType));
  const showResult = searched && Boolean(directRouteResult);
  const showNoData = searched && !directRouteResult && !searchLoading;

  const { data: statesData, loading: statesLoading } = useQuery(
    STATES_BY_COUNTRY,
    {
      variables: { country: INDIA_COUNTRY_ID },
      fetchPolicy: 'cache-first',
    },
  );
  const [fetchCitiesByState] = useLazyQuery(CITY_BY_STATE, {
    fetchPolicy: 'cache-first',
  });

  const handleSearch = async () => {
    if (hasSameCity) {
      setSameCityError(true);
      return;
    }
    setSameCityError(false);
    if (!isValid) return;
    setSearchLoading(true);
    setSearched(true);
    setRequested(false);
    setSearchResponse(null);

    const response = await axiosPost(
      `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/v2/search`,
      {
        originCityId: Number(origin.value),
        destinationCityId: Number(destination.value),
        vehicleSize: vehicleType.value,
      },
      { 'Content-Type': 'application/json' },
      auth,
    );

    setSearchResponse(response);
    setSearchLoading(false);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchVehicleSizes = async () => {
      setVehicleOptionsLoading(true);
      const response = await axiosGetAPI(
        `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/v2/vehicle-sizes`,
        auth,
      );

      if (isMounted && response?.isSuccessful) {
        setVehicleOptions(
          (response?.data?.data || []).map((vehicle) => ({
            value: vehicle.value,
            label: vehicle.label, // Only show "10 MT" not "10 MT - 10 Ton"
            tonnage: vehicle.tonnage,
          })),
        );
      }

      if (isMounted) setVehicleOptionsLoading(false);
    };

    fetchVehicleSizes();

    return () => {
      isMounted = false;
    };
  }, [auth]);

  useEffect(() => {
    if (cachedCityOptions && cachedCityOptions.length > 0) {
      setCityOptions(cachedCityOptions);
      setCitiesLoading(false);
      return undefined;
    }

    const states = statesData?.statesByCountry || [];
    if (!states.length) return undefined;

    let isMounted = true;
    setCitiesLoading(true);

    const loadAllCities = async () => {
      try {
        const cityResults = await Promise.all(
          states.map(async (state) => {
            try {
              const response = await fetchCitiesByState({
                variables: { state: state.id },
              });

              return (response?.data?.citiesByState || []).map((city) => ({
                value: String(city.id),
                cityName: city.cityName,
                stateName: state.stateName,
                stateId: String(state.id),
                label: `${city.cityName} (${state.stateName})`,
              }));
            } catch (err) {
              console.error(`Error loading cities for state ${state.stateName}:`, err);
              return [];
            }
          }),
        );

        const allCities = cityResults
          .flat()
          .sort((a, b) => a.cityName.localeCompare(b.cityName));

        cachedCityOptions = allCities;

        if (isMounted) {
          setCityOptions(allCities);
          setCitiesLoading(false);
        }
      } catch (err) {
        console.error('Error loading all cities:', err);
        if (isMounted) setCitiesLoading(false);
      }
    };

    loadAllCities();

    return () => {
      isMounted = false;
    };
  }, [fetchCitiesByState, statesData]);

  const handleSwap = () => {
    const prev = origin;
    setOrigin(destination);
    setDestination(prev);
    setSearched(false);
    setRequested(false);
    setSearchResponse(null);
  };

  const resetField = (setter) => (val) => {
    setter(val);
    setSameCityError(false);
    setSearched(false);
    setRequested(false);
    setSearchResponse(null);
  };

  const handleAddRequest = async () => {
    setRequestLoading(true);
    try {
      const payload = {
        originCityId: Number(origin.value),
        destinationCityId: Number(destination.value),
        vehicleSize: vehicleType.value,
        remarks: "Rate not available in search",
      };

      const response = await axiosPost(
        `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/v2/request`,
        payload,
        { 'Content-Type': 'application/json' },
        auth
      );

      if (response?.isSuccessful && response?.data?.data) {
        const reqData = response.data.data;
        if (onAddRequest) {
          onAddRequest({
            id: reqData.id,
            origin: reqData.originCityName || originName,
            destination: reqData.destinationCityName || destinationName,
            vehicle: reqData.vehicleSizeLabel || vehicleType?.label,
            requestedBy: reqData.requestedBy || 'You',
            status: reqData.status ? (reqData.status.charAt(0).toUpperCase() + reqData.status.slice(1).toLowerCase()) : 'Pending',
            originalReq: reqData
          });
        }
        setRequested(true);
      }
    } catch (err) {
      console.error("Error creating rate request:", err);
    } finally {
      setRequestLoading(false);
    }
  };

  return (
    <div className="sr-wrap">

      {/* ── Filter Card ─────────────────────────────────────────── */}
      <div className="sr-filter-card">
        <div className="sr-filter-grid">

          {/* Origin */}
          <div className="sr-field">
            <label className="sr-label" htmlFor="search-rate-origin">
              Origin <span className="text-danger">*</span>
            </label>
            <Select
              inputId="search-rate-origin"
              options={cityOptions.filter((city) => !destination || String(city.value) !== String(destination.value))}
              value={origin}
              onChange={resetField(setOrigin)}
              placeholder="Select Origin"
              styles={selectStyles}
              isLoading={statesLoading || citiesLoading}
              isClearable
            />
            {sameCityError && hasSameCity && <span className="sr-validation-error" role="alert">Origin and destination cannot be the same city.</span>}
          </div>

          {/* Swap button */}
          <div className="sr-swap-btn-wrap">
            <button
              type="button"
              className="sr-swap-btn"
              onClick={handleSwap}
              title="Swap Origin & Destination"
              aria-label="Swap origin and destination"
              disabled={!origin && !destination}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 7h16M4 7l4-4M4 7l4 4" />
                <path d="M20 17H4M20 17l-4-4M20 17l-4 4" />
              </svg>
            </button>
          </div>

          {/* Destination */}
          <div className="sr-field">
            <label className="sr-label" htmlFor="search-rate-destination">
              Destination <span className="text-danger">*</span>
            </label>
            <Select
              inputId="search-rate-destination"
              options={cityOptions.filter((city) => !origin || String(city.value) !== String(origin.value))}
              value={destination}
              onChange={resetField(setDestination)}
              placeholder="Select Destination"
              styles={selectStyles}
              isLoading={statesLoading || citiesLoading}
              isClearable
            />
            {sameCityError && hasSameCity && <span className="sr-validation-error" role="alert">Origin and destination cannot be the same city.</span>}
          </div>

          {/* Vehicle Type */}
          <div className="sr-field">
            <label className="sr-label" htmlFor="search-rate-vehicle">
              Vehicle Type <span className="text-danger">*</span>
            </label>
            <Select
              inputId="search-rate-vehicle"
              options={vehicleOptions}
              value={vehicleType}
              onChange={resetField(setVehicleType)}
              placeholder="Select Vehicle Type"
              styles={selectStyles}
              isLoading={vehicleOptionsLoading}
              isDisabled={vehicleOptionsLoading}
              isClearable
            />
          </div>

          {/* Search Button */}
          <div className="sr-field sr-field--btn">
            <ButtonV2
              color="primary"
              style={{ height: '42px', padding: '0 28px', fontSize: '14px', fontWeight: 700, borderRadius: '8px', width: '100%' }}
              onClick={handleSearch}
              disabled={!isValid || searchLoading || hasSameCity}
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </ButtonV2>
          </div>

        </div>
      </div>

      {/* ── Results — data found ─────────────────────────────────── */}
      {showResult && (
        <div className="sr-results-wrap">

          <div className="sr-trip-header">
            <span className="sr-trip-badge sr-trip-badge--origin">{originName}</span>
            <span className="sr-trip-arrow">→</span>
            <span className="sr-trip-badge sr-trip-badge--dest">{destinationName}</span>
            <span className="sr-trip-vehicle">{vehicleType?.label}</span>
          </div>

          <div className="sr-route-list-full">
            {/* Direct Route */}
            {renderRouteWithInfo('Direct', directRouteResult.stops, directRouteResult.routeData)}

            {/* Indirect Routes - Combined in One Card */}
            <div className="sr-route-row">
              <div className="sr-route-card-inline">
                <div className="sr-route-heading">
                  <h5 className="sr-route-title">Indirect Routes</h5>
                  <span className="sr-route-type sr-route-type--indirect">Indirect Routes</span>
                </div>

                {indirectRouteResults.map(({ stops, routeData }, index) => {

                  return (
                    <div key={`indirect-${stops.join('-')}`} style={{ marginTop: index > 0 ? '20px' : '12px', paddingTop: index > 0 ? '20px' : '0', borderTop: index > 0 ? '1px solid #e4e7ec' : 'none' }}>
                      <p className="sr-route-summary" style={{ marginBottom: '12px' }}>
                        Option {index + 1}: Via {stops.slice(1, -1).join(', ')}
                      </p>

                      <div className="sr-route-track">
                        {stops.map((stop, idx) => {
                          const isFirst = idx === 0;
                          const isLast = idx === stops.length - 1;
                          let dotClass = 'sr-route-dot sr-route-dot--mid';
                          if (isFirst) dotClass = 'sr-route-dot sr-route-dot--start';
                          else if (isLast) dotClass = 'sr-route-dot sr-route-dot--end';

                          const segmentInfo = idx < routeData.segments.length ? routeData.segments[idx] : null;

                          return (
                            <React.Fragment key={`indirect-${stops.join('-')}-${stop}`}>
                              <div className="sr-route-stop">
                                <div className={dotClass} />
                                <span className={`sr-route-name ${isFirst || isLast ? 'sr-route-name--bold' : ''}`}>{stop}</span>
                              </div>
                              {idx < stops.length - 1 && (
                                <div className="sr-route-line-wrap">
                                  {segmentInfo && (
                                    <div className="sr-segment-values">
                                      {segmentInfo.distance} • {segmentInfo.time} • {segmentInfo.rate}
                                    </div>
                                  )}
                                  <div className="sr-route-line" />
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>

                      {/* Total for this option - inline */}
                      <div className="sr-option-total sr-option-total--legacy">
                        <span className="sr-option-total-label">Total:</span>
                        <span className="sr-option-total-value">{routeData.total.distance}</span>
                        <span className="sr-option-total-sep">•</span>
                        <span className="sr-option-total-value">{routeData.total.time}</span>
                        <span className="sr-option-total-sep">•</span>
                        <span className="sr-option-total-value sr-option-total-value--rate">{routeData.total.rate}</span>
                        <span className="sr-option-total-sep">•</span>
                        <span className="sr-option-total-value sr-option-total-value--rate">{routeData.total.ratePerKg}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right side - Same format as Direct Route */}
              <div className="sr-route-info-box">
                <div className="sr-indirect-info-list">
                  {indirectRouteResults.map(({ stops, routeData }) => {
                    const { total } = routeData;

                    return (
                      <div className="sr-route-info-stacked sr-indirect-info-option" key={`indirect-info-${stops.join('-')}`}>
                        <div className="sr-indirect-summary-line">
                          <span>{total.distance}</span>
                          <span>•</span>
                          <span>{total.time}</span>
                          <span>•</span>
                          <span className="sr-indirect-summary-line--rate">{total.rate}</span>
                          <span>•</span>
                          <span className="sr-indirect-summary-line--rate">{total.ratePerKg}</span>
                        </div>
                        <div className="sr-info-line">
                          <span className="sr-info-item-label">Distance:</span>
                          <span className="sr-info-item-value">{total.distance}</span>
                          <span className="sr-info-item-sep">•</span>
                          <span className="sr-info-item-label">Time:</span>
                          <span className="sr-info-item-value">{total.time}</span>
                        </div>
                        <div className="sr-info-line">
                          <span className="sr-info-item-label">Rate:</span>
                          <span className="sr-info-item-value sr-info-item-value--rate">{total.rate}</span>
                          <span className="sr-info-item-sep">•</span>
                          <span className="sr-info-item-label">Rate/kg:</span>
                          <span className="sr-info-item-value sr-info-item-value--rate">{total.ratePerKg}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="sr-route-info-stacked sr-indirect-info-placeholder">
                  <div className="sr-info-line">
                    <span className="sr-info-item-label">Distance:</span>
                    <span className="sr-info-item-value">Multiple</span>
                    <span className="sr-info-item-sep">•</span>
                    <span className="sr-info-item-label">Time:</span>
                    <span className="sr-info-item-value">Options</span>
                  </div>
                  <div className="sr-info-line">
                    <span className="sr-info-item-label">Rate:</span>
                    <span className="sr-info-item-value sr-info-item-value--rate">Varies</span>
                    <span className="sr-info-item-sep">•</span>
                    <span className="sr-info-item-label">Rate/kg:</span>
                    <span className="sr-info-item-value sr-info-item-value--rate">Below</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* ── No Data Screen ───────────────────────────────────────── */}
      {showNoData && (
        <>
          <div className="sr-no-data-card">
            <div className="sr-no-data-icon">
              <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="8" y1="11" x2="14" y2="11" />
              </svg>
            </div>
            <p className="sr-no-data-title">No direct rate data available</p>
            <p className="sr-no-data-sub">
              No delivery rate found for <strong>{originName}</strong> → <strong>{destinationName}</strong> via <strong>{vehicleType?.label}</strong>.
            </p>
            <div className="sr-no-data-route">
              <span className="sr-trip-badge sr-trip-badge--origin">{originName}</span>
              <span className="sr-trip-arrow">→</span>
              <span className="sr-trip-badge sr-trip-badge--dest">{destinationName}</span>
              <span className="sr-trip-vehicle">{vehicleType?.label}</span>
            </div>
            {requested ? (
              <div className="sr-no-data-sent">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Request sent! Check <strong>Request Admin</strong> tab.
              </div>
            ) : (
              <ButtonV2
                color="primary"
                style={{ height: '42px', padding: '0 32px', fontSize: '14px', fontWeight: 700, borderRadius: '8px', marginTop: '4px' }}
                onClick={handleAddRequest}
                disabled={requestLoading}
              >
                {requestLoading ? 'Sending...' : '+ Send Request to Request Admin'}
              </ButtonV2>
            )}
          </div>
          <div className="sr-indirect-options">
            <p className="sr-indirect-options-title">Indirect route options</p>
            <div className="sr-route-list-full">
              {indirectRouteResults.map(({ stops, routeData }) => {
                return renderRouteWithInfo('Indirect Option', stops, routeData);
              })}
            </div>
          </div>
        </>
      )
      }

      {/* ── Empty prompt ─────────────────────────────────────────── */}
      {
        !searched && (
          <div className="sr-prompt">
            <div className="sr-prompt-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#a0aec0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <p className="sr-prompt-text">
              Select Origin, Destination &amp; Vehicle Type, then click <strong>Search</strong> to see the delivery rate.
            </p>
          </div>
        )
      }

    </div >
  );
};

export default SearchRateTab;
